import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card } from "./ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { rupee } from "./shared";
import { Plus, Trash2, TrendingUp, Layers, Target, Package, Users, Pencil } from "lucide-react";
import { istNow, ymd } from "../lib/dates";

const Toggle = ({ on, onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`text-xs font-semibold px-3 py-1 rounded-full border ${on ? "bg-green-500/15 text-green-400 border-green-500/50" : "bg-secondary text-muted-foreground border-border"}`}>{on ? "ON" : "OFF"}</button>
);
const label = (k) => (k === "all" ? "All Designations" : k);

export const IncentivePrograms = () => {
  const [cfg, setCfg] = useState(null);
  const [sel, setSel] = useState("all");
  const [saving, setSaving] = useState(false);
  const [period, setPeriod] = useState("month");
  const [report, setReport] = useState(null);
  const isoToday = () => ymd(istNow());
  const [custom, setCustom] = useState({ frm: isoToday().slice(0, 8) + "01", to: isoToday() });

  const load = () => api.get("/incentive-settings").then((r) => setCfg(r.data.current)).catch(() => {});
  useEffect(() => { load(); }, []);

  const range = () => {
    const t = istNow(); const y = t.getFullYear(), m = t.getMonth();
    const iso = (d) => ymd(d);
    if (period === "today") return { frm: iso(t), to: iso(t) };
    if (period === "week") { const s = new Date(t); s.setDate(t.getDate() - 6); return { frm: iso(s), to: iso(t) }; }
    if (period === "month") return { frm: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
    return custom;
  };
  const loadReport = () => { const r = range(); api.get("/incentive-settings/report", { params: { frm: r.frm, to: r.to } }).then((x) => setReport(x.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))); };
  useEffect(() => { if (cfg) loadReport(); /* eslint-disable-next-line */ }, [cfg, period]);

  const save = async () => {
    setSaving(true);
    try { await api.post("/incentive-settings", cfg); toast.success("Incentive programs saved"); await load(); loadReport(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="py-6 text-muted-foreground">Loading incentive programs…</div>;

  const designations = ["all", ...(cfg.designations || Object.keys(cfg.configs).filter((k) => k !== "all"))];
  const prog = cfg.configs[sel] || cfg.configs.all;
  // set a deep path inside cfg.configs[sel]
  const set = (path, val) => setCfg((c) => {
    const n = structuredClone(c); let o = n.configs[sel]; const ks = path.split(".");
    for (let i = 0; i < ks.length - 1; i++) o = o[ks[i]];
    o[ks[ks.length - 1]] = val; return n;
  });
  const setGlobal = (path, val) => setCfg((c) => {
    const n = structuredClone(c); let o = n; const ks = path.split(".");
    for (let i = 0; i < ks.length - 1; i++) o = o[ks[i]];
    o[ks[ks.length - 1]] = val; return n;
  });

  const addDesignation = () => {
    const name = window.prompt("New incentive designation name:");
    if (!name || !name.trim()) return;
    const key = name.trim();
    if (cfg.configs[key]) { toast.error("That designation already exists."); return; }
    setCfg((c) => {
      const n = structuredClone(c);
      // clone the shape of an existing designation with all programs OFF
      const template = structuredClone(n.configs[Object.keys(n.configs).find((k) => k !== "all")] || n.configs.all);
      ["regular", "high_value", "monthly_target"].forEach((cat) => { if (template[cat]) template[cat].enabled = false; });
      n.configs[key] = template;
      n.designations = [...(n.designations || []), key];
      return n;
    });
    setSel(key);
    toast.info("Added. Click Save to persist.");
  };
  const deleteDesignation = (key) => {
    if (key === "all") return;
    if (!window.confirm(`Delete designation "${key}"? Staff assigned to it will fall back to All Designations.`)) return;
    setCfg((c) => {
      const n = structuredClone(c);
      delete n.configs[key];
      n.designations = (n.designations || []).filter((d) => d !== key);
      return n;
    });
    setSel("all");
    toast.info("Removed. Click Save to persist.");
  };
  const renameDesignation = async (key) => {
    if (key === "all") return;
    const nn = window.prompt(`Rename "${key}" to:`, key);
    if (!nn || !nn.trim() || nn.trim() === key) return;
    try { await api.post("/incentive-settings/designation/rename", { old: key, new: nn.trim() }); toast.success("Renamed & staff migrated"); await load(); setSel(nn.trim()); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const tot = report?.rows?.reduce((a, r) => ({
    regular: a.regular + (r.regular_service_incentive || 0),
    product: a.product + (r.product_incentive || 0),
    hv: a.hv + (r.high_value_reward || 0),
    mt: a.mt + (r.monthly_target_bonus || 0),
    total: a.total + (r.total_incentive || 0),
  }), { regular: 0, product: 0, hv: 0, mt: 0, total: 0 });

  return (
    <div className="space-y-6" data-testid="incentive-programs">
      <div className="flex items-start justify-between flex-wrap gap-3 pt-2">
        <p className="text-sm text-muted-foreground max-w-2xl">Configure each incentive program <strong>per designation</strong>. A staff's assigned <strong>Incentive Designation</strong> decides which rules apply; if a program is OFF for their designation it falls back to <strong>All Designations</strong> (never both). Unassigned staff only get All-Designations programs. Editing rules never changes past payslips/reports.</p>
        <Button onClick={save} disabled={saving} data-testid="inc-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37] h-9">{saving ? "Saving…" : "Save Programs"}</Button>
      </div>

      {/* Designation selector */}
      <Card className="p-4 bg-card border-border" data-testid="inc-designation-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground flex items-center gap-1 mr-1"><Users size={14} /> Designation:</span>
          {designations.map((d) => (
            <button key={d} onClick={() => setSel(d)} data-testid={`inc-desig-${d.replace(/\s+/g, "-")}`}
              className={`text-xs px-3 py-1.5 rounded-full border ${sel === d ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-border"}`}>{label(d)}</button>
          ))}
          <Button size="sm" variant="outline" className="border-primary/50 text-primary h-7" onClick={addDesignation} data-testid="inc-desig-add"><Plus size={13} className="mr-1" /> Add</Button>
          {sel !== "all" && (
            <>
              <Button size="sm" variant="ghost" className="h-7 text-muted-foreground" onClick={() => renameDesignation(sel)} data-testid="inc-desig-rename"><Pencil size={13} className="mr-1" /> Rename</Button>
              <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => deleteDesignation(sel)} data-testid="inc-desig-del"><Trash2 size={13} className="mr-1" /> Delete</Button>
            </>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-2">Now editing: <strong className="text-primary">{label(sel)}</strong></p>
      </Card>

      {/* A. Regular slabs */}
      <Card className="p-5 bg-card border-border" data-testid="inc-regular">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg flex items-center gap-2"><TrendingUp size={18} className="text-primary" /> Regular Incentive Slabs — {label(sel)}</h3>
          <Toggle on={prog.regular.enabled} onClick={() => set("regular.enabled", !prog.regular.enabled)} testid="inc-regular-toggle" />
        </div>
        <div className="space-y-2">
          {prog.regular.slabs.map((s, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`inc-reg-slab-${i}`}>
              <Input type="number" value={s.min} onChange={(e) => set(`regular.slabs.${i}.min`, Number(e.target.value))} placeholder="Min ₹" className="bg-secondary w-28 h-9" data-testid={`inc-reg-min-${i}`} />
              <span className="text-muted-foreground">–</span>
              <Input type="number" value={s.max ?? ""} onChange={(e) => set(`regular.slabs.${i}.max`, e.target.value === "" ? null : Number(e.target.value))} placeholder="Max ₹ (blank = open)" className="bg-secondary w-40 h-9" data-testid={`inc-reg-max-${i}`} />
              <Input type="number" value={s.pct} onChange={(e) => set(`regular.slabs.${i}.pct`, Number(e.target.value))} placeholder="%" className="bg-secondary w-20 h-9" data-testid={`inc-reg-pct-${i}`} /><span className="text-muted-foreground">%</span>
              <Toggle on={s.enabled !== false} onClick={() => set(`regular.slabs.${i}.enabled`, s.enabled === false)} testid={`inc-reg-slab-toggle-${i}`} />
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => set("regular.slabs", prog.regular.slabs.filter((_, j) => j !== i))} data-testid={`inc-reg-slab-del-${i}`}><Trash2 size={14} /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="border-primary/50 text-primary" onClick={() => set("regular.slabs", [...prog.regular.slabs, { min: 0, max: null, pct: 0, enabled: true }])} data-testid="inc-reg-add"><Plus size={14} className="mr-1" /> Add New Slab</Button>
        </div>
      </Card>

      {/* B. High value */}
      <Card className="p-5 bg-card border-border" data-testid="inc-highvalue">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg flex items-center gap-2"><Target size={18} className="text-primary" /> High-Value Bill Incentive — {label(sel)}</h3>
          <Toggle on={prog.high_value.enabled} onClick={() => set("high_value.enabled", !prog.high_value.enabled)} testid="inc-hv-toggle" />
        </div>
        <div className="flex flex-wrap items-end gap-4 mb-3">
          <div>
            <Label className="text-xs">Qualifying bill amount ≥ ₹</Label>
            <Input type="number" value={prog.high_value.threshold} onChange={(e) => set("high_value.threshold", Number(e.target.value))} className="bg-secondary w-32 h-9 mt-1" data-testid="inc-hv-threshold" />
          </div>
          <div>
            <Label className="text-xs">Period</Label>
            <Select value={prog.high_value.period || "monthly"} onValueChange={(v) => set("high_value.period", v)}>
              <SelectTrigger className="bg-secondary w-36 h-9 mt-1" data-testid="inc-hv-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="custom">Custom (whole range)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Ranking / tie basis</Label>
            <Select value={prog.high_value.tie_breaker || "count"} onValueChange={(v) => set("high_value.tie_breaker", v)}>
              <SelectTrigger className="bg-secondary w-56 h-9 mt-1" data-testid="inc-hv-tie"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="count">Most qualifying bills (ties → higher business)</SelectItem>
                <SelectItem value="business">Highest qualifying business (ties → more bills)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-2">Winners are ranked each {prog.high_value.period || "monthly"} period <strong>within {label(sel)} staff</strong>. Add one reward per rank (Top 1, Top 2, …).</p>
        <div className="space-y-2">
          {prog.high_value.rewards.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`inc-hv-reward-${i}`}>
              <span className="text-sm w-16">Rank {i + 1}</span>
              <Select value={r.type} onValueChange={(v) => set(`high_value.rewards.${i}.type`, v)}><SelectTrigger className="bg-secondary w-36 h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="fixed">Fixed ₹</SelectItem><SelectItem value="percent">% of business</SelectItem></SelectContent></Select>
              <Input type="number" value={r.value} onChange={(e) => set(`high_value.rewards.${i}.value`, Number(e.target.value))} className="bg-secondary w-28 h-9" data-testid={`inc-hv-reward-val-${i}`} />
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => set("high_value.rewards", prog.high_value.rewards.filter((_, j) => j !== i).map((x, k) => ({ ...x, rank: k + 1 })))} data-testid={`inc-hv-reward-del-${i}`}><Trash2 size={14} /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="border-primary/50 text-primary" onClick={() => set("high_value.rewards", [...prog.high_value.rewards, { rank: prog.high_value.rewards.length + 1, type: "fixed", value: 0 }])} data-testid="inc-hv-add"><Plus size={14} className="mr-1" /> Add Rank Reward</Button>
        </div>
      </Card>

      {/* C. Monthly target */}
      <Card className="p-5 bg-card border-border" data-testid="inc-monthly">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg flex items-center gap-2"><Target size={18} className="text-primary" /> Monthly Business Target — {label(sel)}</h3>
          <Toggle on={prog.monthly_target.enabled} onClick={() => set("monthly_target.enabled", !prog.monthly_target.enabled)} testid="inc-mt-toggle" />
        </div>
        <label className="flex items-center gap-2 text-sm mb-3"><input type="checkbox" checked={!!prog.monthly_target.include_products} onChange={(e) => set("monthly_target.include_products", e.target.checked)} data-testid="inc-mt-products" className="accent-[#C9A24B]" /> Include Product Sales in this designation's Monthly Business Target (default OFF)</label>
        <div className="space-y-2">
          {prog.monthly_target.slabs.map((s, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`inc-mt-slab-${i}`}>
              <Input type="number" value={s.min} onChange={(e) => set(`monthly_target.slabs.${i}.min`, Number(e.target.value))} placeholder="Min ₹" className="bg-secondary w-32 h-9" data-testid={`inc-mt-min-${i}`} />
              <span className="text-muted-foreground">–</span>
              <Input type="number" value={s.max ?? ""} onChange={(e) => set(`monthly_target.slabs.${i}.max`, e.target.value === "" ? null : Number(e.target.value))} placeholder="Max ₹ (blank = open)" className="bg-secondary w-40 h-9" data-testid={`inc-mt-max-${i}`} />
              <Select value={s.type} onValueChange={(v) => set(`monthly_target.slabs.${i}.type`, v)}><SelectTrigger className="bg-secondary w-32 h-9"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="fixed">Fixed ₹</SelectItem><SelectItem value="percent">% of business</SelectItem></SelectContent></Select>
              <Input type="number" value={s.value} onChange={(e) => set(`monthly_target.slabs.${i}.value`, Number(e.target.value))} className="bg-secondary w-28 h-9" data-testid={`inc-mt-val-${i}`} />
              <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => set("monthly_target.slabs", prog.monthly_target.slabs.filter((_, j) => j !== i))} data-testid={`inc-mt-slab-del-${i}`}><Trash2 size={14} /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="border-primary/50 text-primary" onClick={() => set("monthly_target.slabs", [...prog.monthly_target.slabs, { min: 0, max: null, type: "fixed", value: 0 }])} data-testid="inc-mt-add"><Plus size={14} className="mr-1" /> Add Monthly Target Slab</Button>
        </div>
      </Card>

      {/* Product Sale Incentive (GLOBAL) */}
      <Card className="p-5 bg-card border-border" data-testid="inc-product-sale">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-display text-lg flex items-center gap-2"><Package size={18} className="text-primary" /> Product Sale Incentive <span className="text-xs text-muted-foreground font-normal">(applies to all staff)</span></h3>
          <Toggle on={cfg.product_sale.enabled} onClick={() => setGlobal("product_sale.enabled", !cfg.product_sale.enabled)} testid="inc-ps-toggle" />
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Incentive on product sales</Label>
          <Input type="number" value={cfg.product_sale.percent} onChange={(e) => setGlobal("product_sale.percent", Number(e.target.value))} className="bg-secondary w-24 h-9" data-testid="inc-ps-percent" /><span className="text-muted-foreground text-sm">% of product sales</span>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Same percentage for every staff member. When ON, product incentive is paid at this flat % and is not double-counted in the Regular slab.</p>
      </Card>

      {/* Stacking (GLOBAL) */}
      <Card className="p-5 bg-card border-border" data-testid="inc-stacking">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg flex items-center gap-2"><Layers size={18} className="text-primary" /> Combination / Stacking Rules <span className="text-xs text-muted-foreground font-normal">(applies to all designations)</span></h3>
          <Toggle on={cfg.stacking.allow_together} onClick={() => setGlobal("stacking.allow_together", !cfg.stacking.allow_together)} testid="inc-stack-toggle" />
        </div>
        <p className="text-xs text-muted-foreground mt-2">{cfg.stacking.allow_together ? "ON — staff receive Regular + High-Value + Monthly Target combined (plus Product Sale Incentive)." : "OFF — only the highest-priority program applies (Regular → High-Value → Monthly Target). Product Sale Incentive is always paid."}</p>
      </Card>

      {/* Calculation summary */}
      <Card className="p-5 bg-card border-border" data-testid="inc-report">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h3 className="font-display text-lg">Incentive Calculation Summary</h3>
          <div className="flex gap-1">
            {["today", "week", "month", "custom"].map((p) => (
              <button key={p} onClick={() => setPeriod(p)} data-testid={`inc-period-${p}`} className={`text-xs px-3 py-1 rounded-full border capitalize ${period === p ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-muted-foreground border-border"}`}>{p === "week" ? "This Week" : p === "month" ? "This Month" : p === "today" ? "Today" : "Custom"}</button>
            ))}
          </div>
        </div>
        {period === "custom" && (
          <div className="flex items-end gap-2 mb-3">
            <div><Label className="text-xs">From</Label><Input type="date" value={custom.frm} onChange={(e) => setCustom({ ...custom, frm: e.target.value })} className="bg-secondary mt-1 h-9" data-testid="inc-custom-from" /></div>
            <div><Label className="text-xs">To</Label><Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="bg-secondary mt-1 h-9" data-testid="inc-custom-to" /></div>
            <Button onClick={loadReport} className="rounded-sm bg-primary text-primary-foreground h-9" data-testid="inc-custom-run">Run</Button>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-border">
              <th className="py-2 pr-3">Staff</th><th className="pr-3">Designation</th><th className="pr-3">Branch</th>
              <th className="pr-3 text-right">Service Biz</th>
              <th className="pr-3 text-right">Regular Inc.</th>
              <th className="pr-3 text-right">Product Inc.</th>
              <th className="pr-3 text-right">High-Value</th>
              <th className="pr-3 text-right">Monthly Target</th>
              <th className="pr-3 text-right">Final Total</th>
            </tr></thead>
            <tbody>
              {report?.rows?.map((r) => (
                <tr key={r.staff_id} className="border-b border-border/50" data-testid={`inc-report-row-${r.staff_id}`}>
                  <td className="py-2 pr-3 font-medium">{r.staff_name}</td>
                  <td className="pr-3 text-xs text-muted-foreground">{r.designation}</td>
                  <td className="pr-3 text-xs text-muted-foreground">{r.branch}</td>
                  <td className="pr-3 text-right">{rupee(r.service_business)}</td>
                  <td className="pr-3 text-right">{rupee(r.regular_service_incentive ?? r.regular_incentive)}</td>
                  <td className="pr-3 text-right">{rupee(r.product_incentive || 0)}</td>
                  <td className="pr-3 text-right">{rupee(r.high_value_reward)}{r.high_value_rank ? <span className="text-[10px] text-muted-foreground ml-1">#{r.high_value_rank}</span> : null}</td>
                  <td className="pr-3 text-right">{rupee(r.monthly_target_bonus)}</td>
                  <td className="pr-3 text-right font-semibold text-primary">{rupee(r.total_incentive)}</td>
                </tr>
              ))}
              {!report?.rows?.length && <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">No data for this period.</td></tr>}
            </tbody>
            {report?.rows?.length ? (
              <tfoot>
                <tr className="border-t border-border font-semibold" data-testid="inc-report-total">
                  <td className="py-2 pr-3">Total</td><td></td><td></td><td></td>
                  <td className="pr-3 text-right">{rupee(tot.regular)}</td>
                  <td className="pr-3 text-right">{rupee(tot.product)}</td>
                  <td className="pr-3 text-right">{rupee(tot.hv)}</td>
                  <td className="pr-3 text-right">{rupee(tot.mt)}</td>
                  <td className="pr-3 text-right text-primary">{rupee(tot.total)}</td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </Card>
    </div>
  );
};
